import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { io, type Socket } from 'socket.io-client'
import { API_BASE_URL } from '@/lib/apiBase'
import { getAccessToken } from '@/lib/authToken'
import { notificationKeys } from '@/hooks/useNotifications'
import type { Notification } from '@/api/notificationsApi'

// The socket connects to the API ORIGIN (not the `/api` REST prefix). Backend
// gateway namespace: `/ws/notifications`. socket.io CORS on the backend already
// allows the frontend origin.
export function notificationsWsOrigin(): string {
  return API_BASE_URL.replace(/\/api\/?$/, '')
}

export interface NotificationSocketHandlers {
  token: string
  onNew: (n: Notification) => void
  onRead: (payload: unknown) => void
  // Fired on a genuine reconnect (not the first connect) so the client can
  // reconcile anything missed while disconnected.
  onReconnect: () => void
}

// Pure factory (no React) so the wiring is unit-testable by mocking socket.io.
export function createNotificationsSocket(handlers: NotificationSocketHandlers): Socket {
  const socket = io(`${notificationsWsOrigin()}/ws/notifications`, {
    auth: { token: handlers.token },
    transports: ['websocket'],
    forceNew: true,
  })

  let connectedOnce = false
  socket.on('connect', () => {
    if (connectedOnce) handlers.onReconnect()
    connectedOnce = true
  })
  socket.on('notification:new', handlers.onNew)
  socket.on('notification:read', handlers.onRead)

  return socket
}

/**
 * Opens the notifications WebSocket for the signed-in user and keeps the
 * TanStack Query cache fresh: any push (new / read) or reconnect invalidates the
 * shared `notifications` keys, so the bell, sidebar badge, popover, and page all
 * update live. Mount ONCE (in Layout). Disconnects on unmount / logout.
 */
export function useNotificationsSocket(): void {
  const qc = useQueryClient()
  useEffect(() => {
    const token = getAccessToken()
    if (!token) return
    const refresh = () => {
      void qc.invalidateQueries({ queryKey: notificationKeys })
    }
    const socket = createNotificationsSocket({
      token,
      onNew: refresh,
      onRead: refresh,
      onReconnect: refresh,
    })
    return () => {
      socket.disconnect()
    }
  }, [qc])
}
